/**
 * Human-readable labels for the context variables offered by the context node's
 * picker. The backend context map keys (device/org characteristics + document
 * metadata) are terse, machine-oriented ids (`createdAt`, `device-is-uk`); this
 * config maps them to friendly labels shown in the picker, with the raw key kept
 * visible underneath. Keys without an explicit entry fall back to a humanized
 * form of the key (see `getContextLabel`).
 */
export const CONTEXT_LABELS: Record<string, string> = {
  // Document metadata
  documentIdentifier: "Document identifier",
  documentNumber: "Document number",
  revision: "Document revision",
  createdAt: "Document creation date",
  approvedAt: "Document approval date",
  createdBy: "Document author",
  initials: "Author initials",
  "company-name": "Company name",
  "company-description": "Company description",

  // Device characteristics
  "device-name": "Device name",
  "device-description": "Device description",
  "device-has-software": "Device has software",
  "device-has-hardware": "Device has hardware",
  "device-is-software-only": "Device is software only",
  "device-has-sterile-component": "Device has a sterile component",
  "device-has-implantable-component": "Device has an implantable component",
  "device-has-electrical-component": "Device has an electrical component",
  "device-has-mechanical-component": "Device has a mechanical component",
  "device-has-firmware-component": "Device has a firmware component",
  "device-has-ai": "Device has an AI component",
  "device-is-mdr": "Selling device in EU (MDR)",
  "device-is-fda": "Selling device in US (FDA)",
  "device-is-ivdr": "Selling device in EU (IVDR)",
  "device-is-uk": "Selling device in UK",
  "device-is-canada": "Selling device in Canada",

  // Organization-wide device characteristics
  "organization-has-software-devices": "Organization has software devices",
  "organization-has-hardware-devices": "Organization has hardware devices",
  "organization-has-software-only-devices":
    "Organization has software-only devices",
  "organization-has-sterile-component-devices":
    "Organization has devices with a sterile component",
  "organization-has-implantable-component-devices":
    "Organization has devices with an implantable component",
  "organization-has-electrical-component-devices":
    "Organization has devices with an electrical component",
  "organization-has-mechanical-component-devices":
    "Organization has devices with a mechanical component",
  "organization-has-firmware-component-devices":
    "Organization has devices with a firmware component",
  "organization-has-ai-devices":
    "Organization has devices with an AI component",
  "organization-has-mdr-devices": "Organization sells devices in EU (MDR)",
  "organization-has-fda-devices": "Organization sells devices in US (FDA)",
  "organization-has-ivdr-devices": "Organization sells devices in EU (IVDR)",
  "organization-has-uk-devices": "Organization sells devices in UK",
  "organization-has-canada-devices": "Organization sells devices in Canada",
};

/**
 * Turn an unmapped context key into a readable label so the picker always shows
 * a friendly title above the raw key. Handles both `kebab-case` and `camelCase`
 * ids (e.g. `documentStatus` -> "Document status", `foo-bar` -> "Foo bar").
 */
const humanizeContextKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Human-readable label for a context key, falling back to a humanized key. */
export const getContextLabel = (key: string): string =>
  CONTEXT_LABELS[key] ?? humanizeContextKey(key);
